<template>
  <!--
    Mockup de la app Nukli en HTML/CSS con contenido REAL localizado (i18n → 19 idiomas).
    Reproduce fielmente la UI real: rail de iconos, lista con pestañas y chips de resumen IA,
    lector con "Suggested replies", panel de chat IA, y la tabla de "Follow-ups".
    Cuenta demo: Júlia Serra (Estudi Serra). Variantes:
      inbox     · buzón de 4 paneles (hero)
      replies   · lector + respuestas sugeridas por IA
      chat      · panel del asistente IA
      followups · tabla de seguimientos
    El frame mide 1180x740 y se escala con `zoom` al ancho del contenedor.
  -->
  <div class="mockup" ref="root" role="img" :aria-label="label || 'Nukli'">
    <div
      class="mk-frame"
      :class="'is-' + tier"
      :style="{ width: design.w + 'px', height: design.h + 'px', zoom: scale }"
    >
      <div class="mk-bar">
        <span class="mk-dot"></span><span class="mk-dot"></span><span class="mk-dot"></span>
      </div>

      <!-- ===================== INBOX (4 panes) ===================== -->
      <div v-if="variant === 'inbox'" class="mk-body mk-inbox">
        <!-- icon rail -->
        <aside class="mk-rail">
          <div class="mk-logo"><svg viewBox="0 0 24 24"><path d="M12 2 22 12 12 22 2 12Z"/></svg></div>
          <button v-for="it in railTop" :key="it.key" class="mk-rail-btn" :class="{ 'is-active': it.active }">
            <svg viewBox="0 0 24 24"><path :d="it.path"/></svg>
            <span v-if="it.badge" class="mk-rail-badge" :class="'is-' + it.badgeColor">{{ it.badge }}</span>
          </button>
          <div class="mk-rail-sp"></div>
          <button v-for="it in railBottom" :key="it.key" class="mk-rail-btn mk-rail-btn--sm">
            <svg viewBox="0 0 24 24"><path :d="it.path"/></svg>
          </button>
          <span class="mk-pro">Pro+</span>
          <span class="mk-me">D</span>
        </aside>

        <!-- list -->
        <section class="mk-list">
          <div class="mk-list-head">
            <span class="mk-back">‹</span>
            <b>{{ t('listTitle') }}</b>
            <span class="mk-list-actions">
              <span class="mk-circle"><svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg></span>
              <span class="mk-circle mk-circle--accent"><svg viewBox="0 0 24 24"><path d="M4 20h4L18.5 9.5l-4-4L4 16v4Z"/></svg></span>
            </span>
          </div>
          <div class="mk-tabs">
            <span class="mk-tab is-active">{{ t('tabs.all') }}</span>
            <span class="mk-tab"><svg viewBox="0 0 24 24" class="mk-ic-xs"><circle cx="12" cy="12" r="9"/><path d="M12 8v4l3 2"/></svg>{{ t('tabs.pending') }}<em>10</em></span>
            <span class="mk-tab"><svg viewBox="0 0 24 24" class="mk-ic-xs"><circle cx="12" cy="12" r="9"/><path d="m8 12 3 3 5-6"/></svg>{{ t('tabs.done') }}</span>
          </div>
          <div class="mk-sort"><span>⇅ {{ t('sort') }} ⌄</span></div>
          <ul class="mk-rows">
            <li v-for="(row, i) in list" :key="i" class="mk-row" :class="{ 'is-active': listMeta[i].active }">
              <span class="mk-av">{{ listMeta[i].avatar }}</span>
              <div class="mk-row-main">
                <div class="mk-row-top">
                  <span class="mk-row-from">{{ row.from }}</span>
                  <span class="mk-row-time">{{ row.time }}</span>
                </div>
                <div class="mk-row-subj">{{ row.subject }}</div>
                <div class="mk-row-prev">{{ row.preview }}</div>
                <div v-if="row.summary" class="mk-chip-ai" :class="'is-' + listMeta[i].urgency">{{ row.summary }}</div>
              </div>
              <span v-if="listMeta[i].count" class="mk-count">{{ listMeta[i].count }}</span>
            </li>
          </ul>
          <div class="mk-list-foot"><svg viewBox="0 0 24 24" class="mk-ic-xs"><path d="M3 5h18v14H3z M3 8l9 6 9-6"/></svg>{{ t('footer.emails') }} · <i>{{ t('footer.unread') }}</i></div>
        </section>

        <!-- reader -->
        <section class="mk-read">
          <div class="mk-read-head">
            <span class="mk-av mk-av--0">{{ read.initials }}</span>
            <div class="mk-read-who">
              <span><b>{{ read.from }}</b> <i>{{ read.email }}</i></span>
              <span class="mk-read-subj">{{ read.subject }}</span>
            </div>
            <span class="mk-read-tools">
              <i class="mk-t"></i><i class="mk-t"></i><i class="mk-t mk-t--red"></i><i class="mk-t"></i>
            </span>
          </div>
          <div class="mk-read-meta">
            <span>📁 {{ read.folder }}</span><span>{{ read.time }}</span>
          </div>
          <div class="mk-read-body">
            <p v-for="(p, i) in read.body" :key="i">{{ p }}</p>
          </div>
          <div class="mk-suggest">
            <div class="mk-suggest-h"><span class="mk-spark">✦</span>{{ suggested.label }}</div>
            <p class="mk-suggest-hint">{{ suggested.hint }}</p>
            <button v-for="(o, i) in suggested.options" :key="i" class="mk-opt">
              <span class="mk-opt-k">{{ ['A','B','C'][i] }}</span>{{ o }}
            </button>
          </div>
          <div class="mk-actionbar">
            <span class="mk-btn mk-btn--dark">↩ {{ actions.reply }}</span>
            <span class="mk-btn mk-btn--ai"><span class="mk-spark">✦</span>{{ actions.replyAI }}</span>
            <span class="mk-btn mk-btn--ghost">{{ actions.forward }}</span>
            <span class="mk-btn mk-btn--ghost">🅐 {{ actions.translate }}</span>
          </div>
        </section>

        <!-- AI chat -->
        <aside class="mk-chat">
          <div class="mk-chat-head"><span class="mk-spark">✦</span><b>{{ chat.title }}</b><span>⌄</span><span class="mk-chat-x">✕</span></div>
          <div class="mk-chat-msgs">
            <div class="mk-msg mk-msg--u">{{ chat.messages[0].text }}</div>
            <div class="mk-msg mk-msg--ai">{{ chat.messages[1].text }}</div>
          </div>
          <div class="mk-chat-input">
            <span class="mk-openmail">✉ {{ chat.openEmail }} ✕</span>
            <div class="mk-chat-field">{{ chat.placeholder }}</div>
            <div class="mk-chat-foot">
              <span class="mk-model">{{ chat.model }} ⌄</span>
              <span class="mk-quota">◕ 32%</span>
              <span class="mk-send">↑</span>
            </div>
          </div>
        </aside>
      </div>

      <!-- ===================== REPLIES ===================== -->
      <div v-else-if="variant === 'replies'" class="mk-body mk-center">
        <div class="mk-panel mk-read mk-read--solo">
          <div class="mk-read-head">
            <span class="mk-av mk-av--0">{{ read.initials }}</span>
            <div class="mk-read-who">
              <span><b>{{ read.from }}</b> <i>{{ read.email }}</i></span>
              <span class="mk-read-subj">{{ read.subject }}</span>
            </div>
          </div>
          <div class="mk-read-body">
            <p v-for="(p, i) in read.body" :key="i">{{ p }}</p>
          </div>
          <div class="mk-suggest mk-suggest--big">
            <div class="mk-suggest-h"><span class="mk-spark">✦</span>{{ suggested.label }} <span class="mk-spark">✨</span></div>
            <p class="mk-suggest-hint">{{ suggested.hint }}</p>
            <button v-for="(o, i) in suggested.options" :key="i" class="mk-opt">
              <span class="mk-opt-k">{{ ['A','B','C'][i] }}</span>{{ o }}
            </button>
            <button class="mk-opt mk-opt--own"><span class="mk-opt-k">C</span>{{ suggested.ownHint }}<span class="mk-send mk-send--sm">↑</span></button>
          </div>
        </div>
      </div>

      <!-- ===================== CHAT ===================== -->
      <div v-else-if="variant === 'chat'" class="mk-body mk-center">
        <aside class="mk-panel mk-chat mk-chat--solo">
          <div class="mk-chat-head"><span class="mk-spark">✦</span><b>{{ chat.title }}</b><span>⌄</span><span class="mk-chat-x">✕</span></div>
          <div class="mk-chat-msgs">
            <div class="mk-msg mk-msg--u">{{ chat.messages[0].text }}</div>
            <div class="mk-msg mk-msg--ai">{{ chat.messages[1].text }}</div>
          </div>
          <div class="mk-chat-input">
            <span class="mk-openmail">✉ {{ chat.openEmail }} ✕</span>
            <div class="mk-chat-field">{{ chat.placeholder }}</div>
            <div class="mk-chat-foot">
              <span class="mk-model">{{ chat.model }} ⌄</span>
              <span class="mk-quota">◕ 32%</span>
              <span class="mk-mic">🎙</span><span class="mk-clip">📎</span>
              <span class="mk-send">↑</span>
            </div>
          </div>
        </aside>
      </div>

      <!-- ============ AGENT (MCP: consulta datos reales y redacta) ============ -->
      <div v-else-if="variant === 'agent'" class="mk-body mk-center mk-center--agent">
        <aside class="mk-panel mk-agentp">
          <div class="mk-chat-head"><span class="mk-spark">✦</span><b>{{ agent.title }}</b><span>⌄</span><span class="mk-chat-x">✕</span></div>
          <div class="mk-agent-scroll">
            <div class="mk-msg mk-msg--u mk-agent-ask">
              <span class="mk-openmail">✉ {{ agent.emailChip }}</span>
              {{ agent.ask }}
            </div>

            <div class="mk-asteps">
              <div v-for="(s, i) in agent.steps" :key="i" class="mk-astep">
                <span class="mk-astep-ic" :class="{ 'is-brand': agentIcons[i].brand }">
                  <svg viewBox="0 0 24 24"><path :d="agentIcons[i].path"/></svg>
                </span>
                <span class="mk-astep-label">{{ s.label }}</span>
                <em class="mk-astep-val">{{ s.value }}</em>
                <span class="mk-astep-ok"><svg viewBox="0 0 24 24"><path d="m5 13 4 4 10-10"/></svg></span>
              </div>
            </div>

            <div class="mk-msg mk-msg--ai mk-agent-answer">{{ agent.answer }}</div>

            <div class="mk-adraft">
              <div class="mk-adraft-h">
                <span class="mk-spark">✦</span>{{ agent.draft.label }}
                <em class="mk-adraft-time">{{ agent.draft.time }}</em>
              </div>
              <div class="mk-adraft-meta"><span>{{ agent.draft.to }}</span><span>{{ agent.draft.subject }}</span></div>
              <p v-for="(p, i) in agent.draft.body" :key="i">{{ p }}</p>
              <div class="mk-prop-actions">
                <span class="mk-btn mk-btn--reject">✕ {{ agent.draft.discard }}</span>
                <span class="mk-btn mk-btn--accept">✓ {{ agent.draft.send }}</span>
              </div>
            </div>
          </div>
        </aside>
      </div>

      <!-- ===================== QUICK EDIT (AI tracked changes) ===================== -->
      <div v-else-if="variant === 'edit'" class="mk-body mk-center">
        <div class="mk-panel mk-editp">
          <div class="mk-edit-top">
            <span class="mk-edit-to">To <b>{{ read.email }}</b></span>
            <span class="mk-edit-prompt"><span class="mk-spark">✦</span>{{ edit.prompt }}</span>
          </div>
          <div class="mk-edit-body">
            <p>
              <span class="mk-del">{{ edit.removed[0] }}</span>
              <span class="mk-ins">{{ edit.added[0] }}</span>
              <span class="mk-mini mk-mini--apply">{{ edit.apply }}</span><span class="mk-mini">{{ edit.cancel }}</span>
            </p>
            <p>
              <span class="mk-del">{{ edit.removed[1] }}</span>
              <span class="mk-ins">{{ edit.added[1] }}</span>
              <span class="mk-mini mk-mini--apply">{{ edit.apply }}</span><span class="mk-mini">{{ edit.cancel }}</span>
            </p>
            <p class="mk-edit-tail">{{ edit.tail }}</p>
          </div>
          <div class="mk-edit-actions">
            <span class="mk-btn mk-btn--reject">✕ {{ edit.reject }}</span>
            <span class="mk-btn mk-btn--accept">✓ {{ edit.accept }}</span>
          </div>
        </div>
      </div>

      <!-- ===================== PLATFORMS (artistic) ===================== -->
      <div v-else-if="variant === 'platforms'" class="mk-body mk-plat">
        <div class="mk-collab-bg" aria-hidden="true">
          <span class="mk-blob mk-blob--a"></span>
          <span class="mk-blob mk-blob--b"></span>
        </div>
        <svg class="mk-plat-links" viewBox="0 0 900 460" preserveAspectRatio="none" aria-hidden="true">
          <path d="M190 250 C 300 150, 380 130, 450 150" />
          <path d="M710 250 C 600 150, 520 130, 450 150" />
          <path d="M450 330 C 450 380, 450 390, 450 410" />
        </svg>

        <!-- browser (left, back) -->
        <div class="mk-dev mk-dev--browser">
          <div class="mk-dev-bar mk-dev-bar--browser">
            <span class="mk-dot"></span><span class="mk-dot"></span><span class="mk-dot"></span>
            <span class="mk-url">mail.nukliapp.com</span>
          </div>
          <div class="mk-dev-screen"><span class="mk-mini-app"><i></i><i></i><i></i></span></div>
          <span class="mk-dev-tag">
            <svg class="mk-os mk-os--line" viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><path d="M3 12h18 M12 3c2.5 2.6 2.5 15 0 18 M12 3c-2.5 2.6-2.5 15 0 18"/></svg>
            {{ platforms.items[3] }}
          </span>
        </div>

        <!-- windows (right, back) -->
        <div class="mk-dev mk-dev--win">
          <div class="mk-dev-bar mk-dev-bar--win">
            <span class="mk-wctl"></span><span class="mk-wctl"></span><span class="mk-wctl"></span>
          </div>
          <div class="mk-dev-screen"><span class="mk-mini-app"><i></i><i></i><i></i></span></div>
          <span class="mk-dev-tag">
            <svg class="mk-os" viewBox="0 0 24 24"><path d="M3 5.9 10.6 4.8v7.1H3V5.9Zm8.6-1.2L21 3.4v8.5h-9.4V4.7ZM3 12.9h7.6V20L3 18.9v-6Zm8.6 0H21v8.6l-9.4-1.1v-7.5Z"/></svg>
            {{ platforms.items[1] }}
          </span>
        </div>

        <!-- macOS (center, front) -->
        <div class="mk-dev mk-dev--mac">
          <div class="mk-dev-bar">
            <span class="mk-dot"></span><span class="mk-dot"></span><span class="mk-dot"></span>
          </div>
          <div class="mk-dev-screen mk-dev-screen--lg">
            <span class="mk-mini-rail"><i class="is-on"></i><i></i><i></i></span>
            <span class="mk-mini-list"><i class="is-sel"></i><i></i><i></i><i></i></span>
            <span class="mk-mini-read"><i></i><i></i><i class="is-ai"></i></span>
          </div>
          <span class="mk-dev-tag mk-dev-tag--main">
            <svg class="mk-os" viewBox="0 0 24 24"><path d="M16.4 1.4c0 1.1-.4 2.2-1.2 3-.9.9-2 1.4-3 1.3-.2-1.1.4-2.2 1.1-3 .9-.9 2.1-1.4 3.1-1.3Zm4.1 15.7c-.5 1.2-.8 1.7-1.4 2.8-.9 1.5-2.2 3.3-3.8 3.4-1.4 0-1.8-.9-3.7-.9s-2.3.9-3.7.9c-1.6 0-2.8-1.7-3.7-3.2-1.9-3-2.1-8 .3-10.5 1-1.2 2.4-1.9 3.9-1.9 1.5 0 2.5.9 3.8.9 1.2 0 2-.9 3.8-.9 1.3 0 2.7.7 3.7 2-3.3 1.8-2.8 6.5.8 7.4Z"/></svg>
            {{ platforms.items[0] }}
          </span>
        </div>

        <!-- linux (front, small) -->
        <div class="mk-dev mk-dev--linux">
          <div class="mk-dev-bar"><span class="mk-dot"></span><span class="mk-dot"></span></div>
          <div class="mk-dev-screen"><span class="mk-mini-app"><i></i><i></i></span></div>
          <span class="mk-dev-tag">
            <svg class="mk-os mk-os--tux" viewBox="0 0 24 24">
              <path d="M12 1.8c-2.2 0-3.6 1.7-3.6 4 0 .8.1 1.5-.1 2-.3.9-1 1.7-1.6 2.7-1 1.6-1.9 3.3-1.9 5.2 0 1.4.5 2.5 1.4 3.2-.2.4-.4.8-.4 1.2 0 1 1 1.5 2.3 1.7 1.2.2 2.5.3 3.9.3s2.7-.1 3.9-.3c1.3-.2 2.3-.7 2.3-1.7 0-.4-.2-.8-.4-1.2.9-.7 1.4-1.8 1.4-3.2 0-1.9-.9-3.6-1.9-5.2-.6-1-1.3-1.8-1.6-2.7-.2-.5-.1-1.2-.1-2 0-2.3-1.4-4-3.6-4Z"/>
              <circle class="mk-os-eye" cx="10.3" cy="6.4" r="1.15"/>
              <circle class="mk-os-eye" cx="13.7" cy="6.4" r="1.15"/>
              <path class="mk-os-beak" d="M12 7.4c1 0 1.8.5 1.8 1.1S13 9.7 12 9.7s-1.8-.5-1.8-1.2.8-1.1 1.8-1.1Z"/>
              <path class="mk-os-feet" d="M9 20.6c-.7.6-1.9.9-2.6.6-.6-.3-.3-.9.4-1.4l1.6-1.1 .6 1.9Zm6 0c.7.6 1.9.9 2.6.6.6-.3.3-.9-.4-1.4l-1.6-1.1-.6 1.9Z"/>
            </svg>
            {{ platforms.items[2] }}
          </span>
        </div>

        <div class="mk-plat-caption">
          <span class="mk-plat-sync"><span class="mk-spark">✦</span>{{ platforms.sync }}</span>
          <p>{{ platforms.caption }}</p>
        </div>
      </div>

      <!-- ===================== COLLAB (artistic) ===================== -->
      <div v-else-if="variant === 'collab'" class="mk-body mk-collab">
        <div class="mk-collab-bg" aria-hidden="true">
          <span class="mk-blob mk-blob--a"></span>
          <span class="mk-blob mk-blob--b"></span>
          <svg class="mk-collab-arc" viewBox="0 0 600 220" preserveAspectRatio="none"><path d="M50 150 C 180 30, 430 30, 560 140"/></svg>
          <span class="mk-float mk-float--1"><span class="mk-av mk-av--1">PA</span></span>
          <span class="mk-float mk-float--2"><span class="mk-av mk-av--4">JS</span></span>
        </div>
        <div class="mk-collab-card">
          <div class="mk-cc-head">
            <span class="mk-av mk-av--0">{{ read.initials }}</span>
            <div class="mk-read-who">
              <span><b>{{ read.from }}</b></span>
              <span class="mk-cc-subj">{{ read.subject }}</span>
            </div>
            <span class="mk-cc-assign"><span class="mk-av mk-av--2 mk-av--xs">LP</span>{{ collab.assign }}</span>
          </div>
          <div class="mk-cc-team">
            <span class="mk-av mk-av--0 mk-av--xs">JS</span>
            <span class="mk-av mk-av--2 mk-av--xs">LP</span>
            <span class="mk-av mk-av--1 mk-av--xs">PA</span>
            <i>{{ collab.onThread }}</i>
          </div>
          <div class="mk-cc-comments">
            <div v-for="(c, i) in collab.comments" :key="i" class="mk-cc-comment">
              <span class="mk-av mk-av--xs" :class="i === 0 ? 'mk-av--2' : 'mk-av--0'">{{ i === 0 ? 'LP' : 'JS' }}</span>
              <p><b>{{ c.who }}</b> {{ c.text }}</p>
            </div>
            <div class="mk-cc-typing"><span class="mk-av mk-av--2 mk-av--xs">LP</span>{{ collab.typing }}<span class="mk-dots"><i></i><i></i><i></i></span></div>
          </div>
          <div class="mk-cc-draft">
            <div class="mk-cc-draft-h"><span class="mk-spark">✦</span>{{ collab.draftLabel }}</div>
            <p>{{ collab.draft }}</p>
            <span class="mk-btn mk-btn--ai"><span class="mk-spark">✦</span>{{ actions.replyAI }}</span>
          </div>
        </div>
      </div>

      <!-- ===================== TOOLS (MCP connections) ===================== -->
      <div v-else-if="variant === 'tools'" class="mk-body mk-tools">
        <div class="mk-collab-bg" aria-hidden="true">
          <span class="mk-blob mk-blob--a"></span>
          <span class="mk-blob mk-blob--b"></span>
        </div>
        <div class="mk-tools-card">
          <div class="mk-tools-head">
            <span class="mk-spark">✦</span>
            <b>{{ tools.title }}</b>
            <span class="mk-tools-count">{{ tools.connected }}</span>
          </div>
          <div class="mk-tools-grid">
            <span
              v-for="(s, i) in services"
              :key="s"
              class="mk-svc"
              :class="{ 'is-off': i >= 5 }"
            >
              <i class="mk-svc-mark" :class="'is-' + i">{{ s.charAt(0) }}</i>{{ s }}
              <em v-if="i < 5" class="mk-svc-on"></em>
            </span>
          </div>
          <div class="mk-tools-chat">
            <div class="mk-msg mk-msg--u">{{ tools.ask }}</div>
            <div class="mk-msg mk-msg--ai">{{ tools.answer }}</div>
          </div>
          <div class="mk-prop">
            <div class="mk-prop-h"><span class="mk-spark">✦</span>{{ tools.proposal.label }}</div>
            <p class="mk-prop-title">{{ tools.proposal.title }}</p>
            <p class="mk-prop-desc">{{ tools.proposal.desc }}</p>
            <div class="mk-prop-actions">
              <span class="mk-btn mk-btn--reject">✕ {{ tools.proposal.discard }}</span>
              <span class="mk-btn mk-btn--accept">✓ {{ tools.proposal.confirm }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- ===================== FOLLOW-UPS ===================== -->
      <div v-else class="mk-body mk-follow">
        <div class="mk-follow-head">
          <span class="mk-back">‹</span>
          <svg viewBox="0 0 24 24" class="mk-ic-xs"><path d="M5 21V4 M5 4h11l-2 4 2 4H5"/></svg>
          <b>{{ followups.title }}</b>
          <span class="mk-tab is-active mk-tab--pill">{{ t('tabs.pending') }}</span>
          <span class="mk-tab mk-tab--pill">{{ t('tabs.done') }}</span>
          <span class="mk-follow-count">10</span>
          <i class="mk-follow-analyzing">{{ followups.analyzing }}</i>
          <span class="mk-follow-right">↻ {{ followups.refresh }}</span>
          <span class="mk-follow-sort">⇅ {{ followups.sort }} ⌄</span>
        </div>
        <table class="mk-table">
          <thead>
            <tr><th></th><th>{{ followups.cols.when }}</th><th>{{ followups.cols.contact }}</th><th>{{ followups.cols.summary }}</th><th>{{ followups.cols.recommended }}</th></tr>
          </thead>
          <tbody>
            <tr v-for="(r, i) in followups.rows" :key="i">
              <td><span class="mk-check"></span></td>
              <td class="mk-when">{{ r.when }}</td>
              <td class="mk-contact"><span class="mk-udot" :class="'is-' + followMeta[i]"></span>{{ r.contact }}</td>
              <td class="mk-sum">{{ r.summary }}</td>
              <td class="mk-rec">{{ r.recommended }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
// El frame se dibuja a un ancho de diseño fijo y se escala con `zoom` al contenedor.
// Tres niveles: en pantallas pequeñas se usa un lienzo más estrecho (y menos paneles)
// para que el texto siga siendo legible en vez de encoger el escritorio entero.
const DESIGN = {
  sm: { w: 430, h: 640 },
  md: { w: 780, h: 700 },
  lg: { w: 1180, h: 740 },
};

// La variante `agent` encadena la petición, seis pasos de consulta y el borrador:
// no cabe en el alto estándar y recortarla dejaría el borrador a medias, que es
// justo lo que hay que ver. Lienzo propio, más alto.
const DESIGN_AGENT = {
  sm: { w: 430, h: 840 },
  md: { w: 780, h: 870 },
  lg: { w: 1180, h: 870 },
};

export default {
  name: 'AppScreenshot',
  props: {
    label: { type: String, default: '' },
    variant: { type: String, default: 'inbox' }, // inbox | replies | chat | agent | edit | platforms | collab | tools | followups
  },
  data() {
    return {
      cw: 0,
      railTop: [
        { key: 'inbox', active: true, badge: '99+', badgeColor: 'red', path: 'M3 5h18v14H3z M3 8l9 6 9-6' },
        { key: 'flag', badge: '10', badgeColor: 'blue', path: 'M5 21V4 M5 4h11l-2 4 2 4H5' },
        { key: 'people', path: 'M16 20v-1a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v1 M10 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6 M18 11a3 3 0 1 0-2-5' },
        { key: 'gift', path: 'M20 12v9H4v-9 M2 7h20v5H2z M12 7v14 M12 7C11 4 8 4 8 6s2 1 4 1 M12 7c1-3 4-3 4-1s-2 1-4 1' },
      ],
      railBottom: [
        { key: 'bug', path: 'M9 7a3 3 0 0 1 6 0 M7 11h10v3a5 5 0 0 1-10 0z M4 12h3 M17 12h3 M5 8l2 2 M19 8l-2 2' },
        { key: 'moon', path: 'M21 12.8A9 9 0 1 1 11.2 3 7 7 0 0 0 21 12.8Z' },
        { key: 'bell', path: 'M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9 M13.7 21a2 2 0 0 1-3.4 0' },
        { key: 'gear', path: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6 M19.4 13l1.6 1.2-1.5 2.6-1.9-.6a6 6 0 0 1-1.7 1l-.4 2h-3l-.4-2a6 6 0 0 1-1.7-1l-1.9.6L6.5 16.2 8.1 15a6 6 0 0 1 0-2l-1.6-1.2L8 9.2l1.9.6a6 6 0 0 1 1.7-1l.4-2h3l.4 2a6 6 0 0 1 1.7 1l1.9-.6 1.5 2.6L19.4 11a6 6 0 0 1 0 2z' },
      ],
      listMeta: [
        { avatar: 'MV', active: true, count: 1, urgency: 'high' },
        { avatar: 'NE', urgency: 'med' },
        { avatar: 'LP', count: 1 },
        { avatar: 'S' },
        { avatar: 'DW' },
        { avatar: 'F' },
      ],
      followMeta: ['high', 'high', 'med', 'med', 'med', 'low'],
      // Pasos del agente: el icono y el color de marca viven aquí; los textos, en i18n.
      agentIcons: [
        { brand: true, path: 'M3 6h18v12H3z M3 10h18 M7 15h4' },
        { brand: true, path: 'M6 3h12v18l-3-2-3 2-3-2-3 2z M9 8h6 M9 12h6 M9 16h3' },
        { path: 'M16 20v-1a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v1 M10 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6 M18 6v3l2 1' },
        { path: 'M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14 M20.5 20.5 16.5 16.5' },
        { path: 'M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z M14 3v5h5 M9 13h6 M9 17h4' },
        { path: 'M4 20h4L18.5 9.5l-4-4L4 16v4Z M13.5 6.5l4 4' },
      ],
      // Nombres propios: no pasan por i18n. Los cinco primeros salen conectados.
      services: ['Stripe', 'Notion', 'Linear', 'GitHub', 'HubSpot', 'Shopify', 'Jira', 'Zapier'],
    };
  },
  computed: {
    tier() {
      if (!this.cw) return 'lg';
      if (this.cw < 520) return 'sm';
      if (this.cw < 900) return 'md';
      return 'lg';
    },
    design() { return (this.variant === 'agent' ? DESIGN_AGENT : DESIGN)[this.tier]; },
    scale() { return this.cw ? this.cw / this.design.w : 1; },
    list() { return this.t('list'); },
    read() { return this.t('read'); },
    suggested() { return this.t('suggested'); },
    actions() { return this.t('actions'); },
    chat() { return this.t('chat'); },
    followups() { return this.t('followups'); },
    collab() { return this.t('collab'); },
    edit() { return this.t('edit'); },
    platforms() { return this.t('platforms'); },
    tools() { return this.t('tools'); },
    agent() { return this.t('agent'); },
  },
  mounted() {
    this.fit();
    if (typeof ResizeObserver !== 'undefined') {
      this.ro = new ResizeObserver(this.fit);
      this.ro.observe(this.$refs.root);
    } else {
      window.addEventListener('resize', this.fit);
    }
  },
  beforeDestroy() {
    if (this.ro) this.ro.disconnect();
    window.removeEventListener('resize', this.fit);
  },
  methods: {
    t(key) { return this.$t('mockup.' + key); },
    fit() {
      const el = this.$refs.root;
      if (el) this.cw = el.clientWidth;
    },
  },
};
</script>

<style scoped>
.mockup {
  --mk-win: #ffffff;
  --mk-bar: #f3f4f6;
  --mk-rail: #f5f6f8;
  --mk-panel: #ffffff;
  --mk-soft: #f7f8fa;
  --mk-line: rgba(8, 12, 24, 0.08);
  --mk-line-2: rgba(8, 12, 24, 0.05);
  --mk-ink: #101625;
  --mk-ink-2: #4a5160;
  --mk-mut: #9198a6;
  --mk-faint: #c3c8d2;
  --mk-accent: #2663eb;
  --mk-accent-weak: #eaf0ff;
  --mk-red: #ef4444;
  --mk-red-weak: #fdecec;
  --mk-red-ink: #b42318;
  --mk-amber: #f59e0b;
  --mk-amber-weak: #fff3df;
  --mk-amber-ink: #b45309;
  --mk-green: #16a34a;
  --mk-green-weak: #e7f7ec;
  --mk-green-ink: #15803d;
  --mk-chip: #eef1f6;
  --mk-bubble: #eaf1ff;
  position: relative;
  width: 100%;
  overflow: hidden;
  line-height: 1.35;
}
.theme--dark .mockup {
  --mk-win: #0f1114;
  --mk-bar: #15181c;
  --mk-rail: #0b0d10;
  --mk-panel: #101317;
  --mk-soft: #15181c;
  --mk-line: rgba(255, 255, 255, 0.09);
  --mk-line-2: rgba(255, 255, 255, 0.05);
  --mk-ink: #e8eaee;
  --mk-ink-2: #b3b9c4;
  --mk-mut: #7c828e;
  --mk-faint: #4b515c;
  --mk-accent: #5a9bff;
  --mk-accent-weak: #16233b;
  --mk-red-weak: #2a1616;
  --mk-red-ink: #ff8a80;
  --mk-amber-weak: #2a2110;
  --mk-amber-ink: #ffc46b;
  --mk-green: #4ade80;
  --mk-green-weak: #12251a;
  --mk-green-ink: #86e6ab;
  --mk-chip: #1b1f25;
  --mk-bubble: #17233c;
}

.mk-frame {
  background: var(--mk-win);
  border: 1px solid var(--mk-line);
  border-radius: 16px;
  overflow: hidden;
  color: var(--mk-ink);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 13px;
  text-align: left;
}
.mk-bar { height: 34px; background: var(--mk-win); display: flex; align-items: center; gap: 8px; padding: 0 15px; border-bottom: 1px solid var(--mk-line-2); }
.mk-dot { width: 11px; height: 11px; border-radius: 50%; }
.mk-dot:nth-child(1) { background: #ff5f57; }
.mk-dot:nth-child(2) { background: #febc2e; }
.mk-dot:nth-child(3) { background: #28c840; }

.mk-body { height: calc(100% - 34px); }
.mk-inbox { display: grid; grid-template-columns: 58px 320px 1fr 330px; }
svg { fill: none; stroke: currentColor; stroke-width: 1.7; stroke-linecap: round; stroke-linejoin: round; }
.mk-ic-xs { width: 13px; height: 13px; }

/* ---- rail ---- */
.mk-rail { background: var(--mk-rail); border-right: 1px solid var(--mk-line); display: flex; flex-direction: column; align-items: center; padding: 14px 0; gap: 14px; }
.mk-logo { width: 30px; height: 30px; border-radius: 9px; background: linear-gradient(135deg, #2663eb, #4f8bff); color: #fff; display: flex; align-items: center; justify-content: center; }
.mk-logo svg { width: 17px; height: 17px; fill: #fff; stroke: none; }
.mk-rail-btn { position: relative; width: 34px; height: 34px; border: 0; background: transparent; border-radius: 9px; color: var(--mk-mut); display: flex; align-items: center; justify-content: center; padding: 0; }
.mk-rail-btn svg { width: 20px; height: 20px; }
.mk-rail-btn.is-active { background: var(--mk-accent-weak); color: var(--mk-accent); }
.mk-rail-btn--sm svg { width: 18px; height: 18px; }
.mk-rail-badge { position: absolute; top: -3px; right: -4px; min-width: 15px; height: 15px; padding: 0 3px; border-radius: 8px; font-size: 8.5px; font-weight: 700; color: #fff; display: flex; align-items: center; justify-content: center; }
.mk-rail-badge.is-red { background: var(--mk-red); }
.mk-rail-badge.is-blue { background: var(--mk-accent); }
.mk-rail-sp { flex: 1; }
.mk-pro { font-size: 8px; font-weight: 700; color: var(--mk-accent); background: var(--mk-accent-weak); padding: 2px 6px; border-radius: 6px; }
.mk-me { width: 28px; height: 28px; border-radius: 50%; background: var(--mk-accent); color: #fff; font-size: 12px; font-weight: 700; display: flex; align-items: center; justify-content: center; }

/* ---- list ---- */
.mk-list { background: var(--mk-panel); border-right: 1px solid var(--mk-line); display: flex; flex-direction: column; overflow: hidden; }
.mk-list-head { display: flex; align-items: center; gap: 9px; padding: 14px 15px 10px; }
.mk-list-head b { font-size: 16px; }
.mk-back { color: var(--mk-mut); font-size: 17px; }
.mk-list-actions { margin-left: auto; display: flex; gap: 8px; }
.mk-circle { width: 30px; height: 30px; border-radius: 50%; background: var(--mk-chip); display: flex; align-items: center; justify-content: center; color: var(--mk-ink-2); }
.mk-circle svg { width: 15px; height: 15px; }
.mk-circle--accent { background: var(--mk-accent); color: #fff; }
.mk-circle--accent svg { fill: #fff; stroke: #fff; stroke-width: 0.6; }
.mk-tabs { display: flex; gap: 6px; padding: 0 15px 10px; }
.mk-tab { display: flex; align-items: center; gap: 4px; font-size: 12.5px; color: var(--mk-mut); font-weight: 500; padding-bottom: 6px; border-bottom: 2px solid transparent; }
.mk-tab.is-active { color: var(--mk-accent); border-bottom-color: var(--mk-accent); font-weight: 600; }
.mk-tab em { font-style: normal; background: var(--mk-chip); padding: 0 5px; border-radius: 6px; font-size: 11px; }
.mk-sort { padding: 8px 15px; border-top: 1px solid var(--mk-line-2); border-bottom: 1px solid var(--mk-line-2); color: var(--mk-mut); font-size: 12px; }
.mk-rows { list-style: none; margin: 0; padding: 0; flex: 1; overflow: hidden; }
.mk-row { display: flex; gap: 10px; padding: 12px 15px; border-bottom: 1px solid var(--mk-line-2); }
.mk-row.is-active { background: var(--mk-accent); border-radius: 12px; margin: 4px 8px; padding: 12px; border-bottom: 0; }
.mk-row.is-active .mk-row-from, .mk-row.is-active .mk-row-subj { color: #fff; }
.mk-row.is-active .mk-row-time, .mk-row.is-active .mk-row-prev { color: rgba(255, 255, 255, 0.8); }
.mk-row.is-active .mk-av { background: rgba(255, 255, 255, 0.22); color: #fff; }
.mk-av { width: 34px; height: 34px; border-radius: 50%; flex: none; background: var(--mk-chip); color: var(--mk-ink-2); font-size: 12px; font-weight: 700; display: flex; align-items: center; justify-content: center; }
.mk-av--0 { background: var(--mk-accent); color: #fff; }
.mk-row-main { min-width: 0; flex: 1; }
.mk-row-top { display: flex; align-items: baseline; gap: 8px; }
.mk-row-from { font-size: 13px; font-weight: 600; flex: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.mk-row-time { font-size: 11px; color: var(--mk-mut); flex: none; }
.mk-row-subj { font-size: 12.5px; margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.mk-row-prev { font-size: 12px; color: var(--mk-mut); margin-top: 1px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.mk-chip-ai { font-size: 11.5px; margin-top: 8px; padding: 6px 9px; border-radius: 8px; line-height: 1.4; }
.mk-chip-ai.is-high { background: var(--mk-red-weak); color: var(--mk-red-ink); }
.mk-chip-ai.is-med { background: var(--mk-amber-weak); color: var(--mk-amber-ink); }
.mk-count { flex: none; align-self: center; min-width: 18px; height: 18px; padding: 0 5px; border-radius: 9px; background: var(--mk-accent); color: #fff; font-size: 11px; font-weight: 700; display: flex; align-items: center; justify-content: center; }
.mk-row.is-active .mk-count { background: #fff; color: var(--mk-accent); }
.mk-list-foot { display: flex; align-items: center; gap: 6px; padding: 11px 15px; border-top: 1px solid var(--mk-line); font-size: 12px; color: var(--mk-mut); }
.mk-list-foot i { color: var(--mk-accent); font-style: normal; }

/* ---- reader ---- */
.mk-read { background: var(--mk-win); display: flex; flex-direction: column; overflow: hidden; }
.mk-read-head { display: flex; align-items: center; gap: 11px; padding: 16px 20px 14px; border-bottom: 1px solid var(--mk-line-2); }
.mk-read-who { display: flex; flex-direction: column; min-width: 0; flex: 1; gap: 2px; }
.mk-read-who b { font-weight: 600; }
.mk-read-who i { color: var(--mk-mut); font-style: normal; font-size: 12px; }
.mk-read-subj { font-size: 15px; font-weight: 700; letter-spacing: -0.01em; }
.mk-read-tools { display: flex; gap: 7px; }
.mk-t { width: 26px; height: 26px; border-radius: 50%; background: var(--mk-chip); }
.mk-t--red { background: var(--mk-red-weak); }
.mk-read-meta { display: flex; justify-content: space-between; padding: 10px 20px; color: var(--mk-mut); font-size: 12px; }
.mk-read-body { padding: 6px 20px 14px; flex: 1; overflow: hidden; }
.mk-read-body p { margin: 0 0 9px; font-size: 13px; color: var(--mk-ink-2); line-height: 1.55; }
.mk-suggest { margin: 0 16px; padding: 14px 16px; border-top: 1px solid var(--mk-line-2); }
.mk-suggest-h { display: flex; align-items: center; gap: 7px; font-weight: 700; font-size: 13px; color: var(--mk-accent); }
.mk-suggest-hint { margin: 5px 0 11px; font-size: 11.5px; color: var(--mk-mut); }
.mk-spark { color: var(--mk-accent); }
.mk-opt { display: flex; align-items: center; gap: 10px; width: 100%; text-align: left; border: 0; background: var(--mk-accent-weak); color: var(--mk-ink); border-radius: 11px; padding: 12px 14px; font-size: 12.5px; font-weight: 500; margin-bottom: 9px; font-family: inherit; }
.mk-opt-k { width: 20px; height: 20px; border-radius: 50%; background: var(--mk-win); color: var(--mk-accent); font-size: 11px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex: none; }
.mk-opt--own { background: var(--mk-soft); color: var(--mk-mut); }
.mk-opt--own .mk-opt-k { color: var(--mk-mut); }
.mk-actionbar { display: flex; align-items: center; gap: 9px; padding: 13px 20px; border-top: 1px solid var(--mk-line); }
.mk-btn { font-size: 12.5px; font-weight: 600; padding: 9px 15px; border-radius: 10px; display: inline-flex; align-items: center; gap: 6px; }
.mk-btn--dark { background: #1a1f2b; color: #fff; }
.theme--dark .mk-btn--dark { background: #2a3040; }
.mk-btn--ai { background: var(--mk-win); color: var(--mk-accent); border: 1.5px solid transparent; background-image: linear-gradient(var(--mk-win), var(--mk-win)), linear-gradient(90deg, #2663eb, #9b5cff, #ff5c8a); background-origin: border-box; background-clip: padding-box, border-box; }
.mk-btn--ghost { color: var(--mk-ink-2); }

/* ---- chat ---- */
.mk-chat { background: var(--mk-soft); border-left: 1px solid var(--mk-line); display: flex; flex-direction: column; }
.mk-chat-head { display: flex; align-items: center; gap: 7px; padding: 15px 16px; font-size: 13.5px; border-bottom: 1px solid var(--mk-line-2); }
.mk-chat-head b { font-weight: 600; }
.mk-chat-head span:nth-child(3) { color: var(--mk-mut); }
.mk-chat-x { margin-left: auto; color: var(--mk-mut); }
.mk-chat-msgs { flex: 1; padding: 16px; display: flex; flex-direction: column; gap: 12px; overflow: hidden; }
.mk-msg { max-width: 84%; padding: 11px 13px; border-radius: 14px; font-size: 12.5px; line-height: 1.5; }
.mk-msg--u { align-self: flex-end; background: var(--mk-bubble); color: var(--mk-ink); border-bottom-right-radius: 5px; }
.mk-msg--ai { align-self: flex-start; background: var(--mk-panel); border: 1px solid var(--mk-line); color: var(--mk-ink-2); border-bottom-left-radius: 5px; }
.mk-chat-input { padding: 12px 14px 14px; border-top: 1px solid var(--mk-line-2); }
.mk-openmail { display: inline-flex; font-size: 11px; color: var(--mk-accent); background: var(--mk-accent-weak); padding: 4px 9px; border-radius: 8px; margin-bottom: 9px; }
.mk-chat-field { background: var(--mk-panel); border: 1px solid var(--mk-line); border-radius: 12px; padding: 11px 13px; color: var(--mk-mut); font-size: 12.5px; }
.mk-chat-foot { display: flex; align-items: center; gap: 12px; margin-top: 11px; padding: 0 2px; color: var(--mk-mut); font-size: 12px; }
.mk-model { font-weight: 600; color: var(--mk-ink-2); }
.mk-send { margin-left: auto; width: 30px; height: 30px; border-radius: 50%; background: var(--mk-accent); color: #fff; display: flex; align-items: center; justify-content: center; font-weight: 700; }
.mk-send--sm { margin-left: auto; width: 22px; height: 22px; font-size: 11px; }
.mk-mic, .mk-clip { font-size: 13px; }

/* ---- centered single-panel variants ---- */
.mk-center { display: flex; align-items: stretch; justify-content: center; background: var(--mk-soft); padding: 26px; }
.mk-panel { width: 100%; max-width: 720px; border: 1px solid var(--mk-line); border-radius: 16px; box-shadow: 0 20px 50px rgba(8, 12, 24, 0.08); overflow: hidden; }
.theme--dark .mk-panel { box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5); }
.mk-read--solo { background: var(--mk-win); }
.mk-suggest--big { margin: 0; padding: 20px 22px 22px; }
.mk-suggest--big .mk-suggest-h { font-size: 15px; }
.mk-suggest--big .mk-opt { padding: 14px 16px; font-size: 13.5px; }
.mk-chat--solo { max-width: 520px; background: var(--mk-soft); display: flex; flex-direction: column; }

/* ---- quick edit (AI tracked changes) ---- */
.mk-editp { max-width: 640px; background: var(--mk-win); display: flex; flex-direction: column; }
.mk-edit-top { display: flex; align-items: center; gap: 12px; padding: 16px 20px; border-bottom: 1px solid var(--mk-line-2); }
.mk-edit-to { font-size: 13px; color: var(--mk-mut); }
.mk-edit-to b { color: var(--mk-ink); font-weight: 600; }
.mk-edit-prompt { margin-left: auto; display: inline-flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 600; color: var(--mk-accent); background: var(--mk-accent-weak); padding: 5px 11px; border-radius: 999px; }
.mk-edit-body { padding: 18px 20px; font-size: 14px; line-height: 1.85; }
.mk-del { color: var(--mk-red-ink); text-decoration: line-through; text-decoration-color: rgba(180, 35, 24, 0.55); background: var(--mk-red-weak); border-radius: 4px; padding: 1px 3px; }
.mk-ins { color: var(--mk-green-ink); background: var(--mk-green-weak); border-radius: 4px; padding: 1px 3px; margin-left: 4px; }
.mk-mini { font-size: 11px; font-weight: 600; color: var(--mk-mut); padding: 2px 8px; border-radius: 6px; margin-left: 6px; vertical-align: middle; }
.mk-mini--apply { background: #1a1f2b; color: #fff; }
.theme--dark .mk-mini--apply { background: #2a3040; }
.mk-edit-tail { color: var(--mk-ink); }
.mk-edit-actions { margin-top: auto; display: flex; justify-content: flex-end; gap: 10px; padding: 14px 20px; border-top: 1px solid var(--mk-line); }
.mk-btn--reject { background: transparent; color: var(--mk-red-ink); border: 1px solid var(--mk-line); }
.mk-btn--accept { background: var(--mk-green); color: #fff; }

/* ---- platforms (artistic) ---- */
.mk-plat { position: relative; background: var(--mk-soft); overflow: hidden; }
.mk-plat-links { position: absolute; left: 0; top: 4%; width: 100%; height: 66%; }
.mk-plat-links path { fill: none; stroke: var(--mk-accent); stroke-width: 2; stroke-dasharray: 5 10; opacity: 0.38; }
.mk-dev { position: absolute; border-radius: 14px; background: var(--mk-win); border: 1px solid var(--mk-line); overflow: visible; }
.mk-dev-bar { height: 24px; display: flex; align-items: center; gap: 5px; padding: 0 10px; background: var(--mk-bar); border-radius: 13px 13px 0 0; border-bottom: 1px solid var(--mk-line-2); }
.mk-dev-bar .mk-dot { width: 7px; height: 7px; }
.mk-dev-bar--browser .mk-url { flex: 1; margin-left: 6px; font-size: 8px; color: var(--mk-mut); background: var(--mk-win); border-radius: 999px; padding: 3px 8px; text-align: center; }
.mk-dev-bar--win { justify-content: flex-end; }
.mk-wctl { width: 8px; height: 8px; border-radius: 2px; background: var(--mk-faint); }
.mk-dev-screen { padding: 10px; display: flex; gap: 6px; height: calc(100% - 24px); border-radius: 0 0 13px 13px; }
.mk-mini-app { display: flex; flex-direction: column; gap: 6px; width: 100%; }
.mk-mini-app i { display: block; height: 9px; border-radius: 3px; background: var(--mk-chip); }
.mk-mini-app i:first-child { background: var(--mk-accent-weak); }
.mk-mini-rail { display: flex; flex-direction: column; gap: 5px; width: 16px; flex: none; }
.mk-mini-rail i { display: block; height: 16px; border-radius: 5px; background: var(--mk-chip); }
.mk-mini-rail i.is-on { background: var(--mk-accent); }
.mk-mini-list { display: flex; flex-direction: column; gap: 5px; width: 34%; flex: none; }
.mk-mini-list i { display: block; height: 20px; border-radius: 5px; background: var(--mk-chip); }
.mk-mini-list i.is-sel { background: var(--mk-accent); }
.mk-mini-read { display: flex; flex-direction: column; gap: 6px; flex: 1; }
.mk-mini-read i { display: block; height: 9px; border-radius: 3px; background: var(--mk-chip); }
.mk-mini-read i.is-ai { height: 30px; margin-top: auto; background: var(--mk-accent-weak); border: 1px solid var(--mk-accent); }
.mk-dev-tag { position: absolute; left: 50%; transform: translateX(-50%); bottom: -13px; display: inline-flex; align-items: center; gap: 5px; font-size: 10px; font-weight: 700; letter-spacing: 0.03em; color: var(--mk-ink-2); background: var(--mk-win); border: 1px solid var(--mk-line); padding: 4px 11px; border-radius: 999px; white-space: nowrap; }
.mk-dev-tag--main { color: #fff; background: var(--mk-accent); border-color: transparent; }
.mk-os { width: 12px; height: 12px; flex: none; fill: currentColor; stroke: none; }
.mk-os--line { fill: none; stroke: currentColor; stroke-width: 1.8; }
.mk-os--tux .mk-os-eye { fill: var(--mk-win); }
.mk-dev-tag--main .mk-os--tux .mk-os-eye { fill: var(--mk-accent); }
.mk-os--tux .mk-os-beak, .mk-os--tux .mk-os-feet { fill: #f5a623; }
.mk-dev--mac { left: 50%; top: 12%; width: 340px; height: 216px; transform: translateX(-50%); box-shadow: 0 34px 66px rgba(8, 12, 24, 0.20); z-index: 3; }
.mk-dev--browser { left: 4%; top: 27%; width: 214px; height: 142px; transform: rotate(-5deg); box-shadow: 0 22px 44px rgba(8, 12, 24, 0.13); z-index: 2; }
.mk-dev--win { right: 4%; top: 27%; width: 214px; height: 142px; transform: rotate(5deg); box-shadow: 0 22px 44px rgba(8, 12, 24, 0.13); z-index: 2; }
.mk-dev--linux { left: 50%; top: 60%; width: 152px; height: 98px; transform: translateX(-50%); box-shadow: 0 18px 36px rgba(8, 12, 24, 0.12); z-index: 4; }
.theme--dark .mk-dev { box-shadow: 0 30px 60px rgba(0, 0, 0, 0.55); }
.mk-plat-caption { position: absolute; left: 0; right: 0; bottom: 26px; text-align: center; }
.mk-plat-sync { display: inline-flex; align-items: center; gap: 6px; font-size: 11.5px; font-weight: 700; color: var(--mk-accent); background: var(--mk-accent-weak); padding: 5px 13px; border-radius: 999px; }
.mk-plat-caption p { margin: 9px 0 0; font-size: 12.5px; color: var(--mk-mut); }

/* ---- collaborators (artistic) ---- */
.mk-collab { position: relative; background: var(--mk-soft); overflow: hidden; display: flex; align-items: center; justify-content: center; padding: 40px; }
.mk-collab-bg { position: absolute; inset: 0; }
.mk-blob { position: absolute; border-radius: 50%; filter: blur(55px); opacity: 0.55; }
.mk-blob--a { width: 340px; height: 340px; left: -70px; top: -60px; background: radial-gradient(circle, #cfe0ff, transparent 70%); }
.mk-blob--b { width: 380px; height: 380px; right: -90px; bottom: -80px; background: radial-gradient(circle, #e7dcff, transparent 70%); }
.theme--dark .mk-blob { opacity: 0.35; }
.theme--dark .mk-blob--a { background: radial-gradient(circle, #24406e, transparent 70%); }
.theme--dark .mk-blob--b { background: radial-gradient(circle, #3a2c66, transparent 70%); }
.mk-collab-arc { position: absolute; left: 7%; top: 34%; width: 86%; height: 34%; }
.mk-collab-arc path { fill: none; stroke: var(--mk-accent); stroke-width: 2; stroke-dasharray: 4 9; opacity: 0.4; }
.mk-float { position: absolute; }
.mk-float--1 { left: 8%; top: 52%; }
.mk-float--2 { right: 9%; top: 44%; }
.mk-float .mk-av { width: 46px; height: 46px; font-size: 15px; border: 3px solid var(--mk-win); box-shadow: 0 12px 26px rgba(8, 12, 24, 0.18); }
.mk-collab-card { position: relative; width: 100%; max-width: 560px; background: var(--mk-win); border: 1px solid var(--mk-line); border-radius: 20px; box-shadow: 0 34px 70px rgba(8, 12, 24, 0.16); padding: 22px 24px; }
.theme--dark .mk-collab-card { box-shadow: 0 34px 70px rgba(0, 0, 0, 0.55); }
.mk-cc-head { display: flex; align-items: center; gap: 12px; padding-bottom: 15px; border-bottom: 1px solid var(--mk-line-2); }
.mk-cc-subj { font-size: 14px; font-weight: 700; letter-spacing: -0.01em; }
.mk-cc-assign { margin-left: auto; display: inline-flex; align-items: center; gap: 7px; font-size: 11.5px; font-weight: 600; color: var(--mk-accent); background: var(--mk-accent-weak); padding: 4px 11px 4px 4px; border-radius: 999px; }
.mk-av--xs { width: 22px; height: 22px; font-size: 9px; }
.mk-cc-team { display: flex; align-items: center; padding: 15px 0 6px; }
.mk-cc-team .mk-av { margin-right: -7px; border: 2px solid var(--mk-win); }
.mk-cc-team i { font-style: normal; font-size: 12px; color: var(--mk-mut); margin-left: 16px; }
.mk-cc-comments { display: flex; flex-direction: column; gap: 11px; padding: 10px 0 6px; }
.mk-cc-comment { display: flex; gap: 10px; align-items: flex-start; }
.mk-cc-comment p { margin: 0; font-size: 12.5px; color: var(--mk-ink-2); line-height: 1.5; background: var(--mk-soft); padding: 9px 12px; border-radius: 12px; border-top-left-radius: 4px; }
.mk-cc-comment p b { color: var(--mk-ink); font-weight: 600; }
.mk-cc-typing { display: flex; align-items: center; gap: 8px; font-size: 12px; color: var(--mk-mut); padding-left: 2px; }
.mk-dots { display: inline-flex; gap: 3px; }
.mk-dots i { width: 5px; height: 5px; border-radius: 50%; background: var(--mk-faint); }
.mk-cc-draft { margin-top: 15px; padding: 14px 16px; border-radius: 14px; background: var(--mk-accent-weak); }
.mk-cc-draft-h { display: flex; align-items: center; gap: 7px; font-size: 12.5px; font-weight: 700; color: var(--mk-accent); margin-bottom: 8px; }
.mk-cc-draft p { margin: 0 0 13px; font-size: 12.5px; color: var(--mk-ink); line-height: 1.55; }

/* ---- tools / conexiones MCP (artistic) ---- */
.mk-tools { position: relative; background: var(--mk-soft); overflow: hidden; display: flex; align-items: center; justify-content: center; padding: 34px; }
.mk-tools-card { position: relative; width: 100%; max-width: 560px; background: var(--mk-win); border: 1px solid var(--mk-line); border-radius: 20px; box-shadow: 0 34px 70px rgba(8, 12, 24, 0.16); padding: 20px 22px 22px; }
.theme--dark .mk-tools-card { box-shadow: 0 34px 70px rgba(0, 0, 0, 0.55); }
.mk-tools-head { display: flex; align-items: center; gap: 8px; font-size: 14px; padding-bottom: 14px; border-bottom: 1px solid var(--mk-line-2); }
.mk-tools-count { margin-left: auto; font-size: 11.5px; font-weight: 600; color: var(--mk-accent); background: var(--mk-accent-weak); padding: 4px 11px; border-radius: 999px; }
.mk-tools-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px; padding: 14px 0 4px; }
.mk-svc { display: flex; align-items: center; gap: 6px; font-size: 11px; font-weight: 600; color: var(--mk-ink-2); background: var(--mk-soft); border: 1px solid var(--mk-line-2); border-radius: 10px; padding: 8px 9px; overflow: hidden; white-space: nowrap; }
.mk-svc.is-off { opacity: 0.45; }
.mk-svc-mark { flex: none; width: 18px; height: 18px; border-radius: 6px; display: flex; align-items: center; justify-content: center; font-style: normal; font-size: 10px; font-weight: 700; color: #fff; background: var(--mk-faint); }
.mk-svc-mark.is-0 { background: #635bff; }
.mk-svc-mark.is-1 { background: #101625; }
.mk-svc-mark.is-2 { background: #5e6ad2; }
.mk-svc-mark.is-3 { background: #24292f; }
.mk-svc-mark.is-4 { background: #ff7a59; }
/* los dos monogramas casi negros desaparecen sobre el panel oscuro: se invierten,
   como hacen las propias marcas con sus logos monocromos. */
.theme--dark .mk-svc-mark.is-1, .theme--dark .mk-svc-mark.is-3 { background: #e8eaee; color: #101625; }
/* el punto de "conectado" va en el flujo, empujado a la derecha: absoluto se
   solapaba con el nombre más largo (HubSpot), que llena el chip entero. */
.mk-svc-on { flex: none; margin-left: auto; width: 6px; height: 6px; border-radius: 50%; background: var(--mk-green); }
.mk-tools-chat { display: flex; flex-direction: column; gap: 9px; padding: 16px 0 4px; }
.mk-prop { margin-top: 14px; padding: 14px 16px; border-radius: 14px; background: var(--mk-accent-weak); }
.mk-prop-h { display: flex; align-items: center; gap: 7px; font-size: 12px; font-weight: 700; color: var(--mk-accent); margin-bottom: 9px; }
.mk-prop-title { margin: 0 0 4px; font-size: 13px; font-weight: 700; color: var(--mk-ink); }
.mk-prop-desc { margin: 0 0 13px; font-size: 12.5px; color: var(--mk-ink-2); line-height: 1.5; }
.mk-prop-actions { display: flex; justify-content: flex-end; gap: 9px; }

/* ---- agente: pasos de consulta (MCP) + borrador ---- */
/* el panel se ajusta a su contenido en vez de estirarse: si un idioma ocupa menos,
   el aire sobrante queda repartido arriba y abajo y no como un hueco al final. */
.mk-center--agent { align-items: center; }
.mk-agentp { max-width: 560px; max-height: 100%; background: var(--mk-soft); display: flex; flex-direction: column; }
.mk-agent-scroll { display: flex; flex-direction: column; padding: 14px 16px 16px; gap: 11px; overflow: hidden; }
.mk-agent-ask { max-width: 92%; display: flex; flex-direction: column; align-items: flex-start; }
.mk-agent-ask .mk-openmail { margin-bottom: 7px; }
.mk-agent-answer { max-width: 100%; }

.mk-asteps { display: flex; flex-direction: column; gap: 5px; }
.mk-astep {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 8px 11px;
  background: var(--mk-panel);
  border: 1px solid var(--mk-line-2);
  border-radius: 11px;
  font-size: 12px;
}
.mk-astep-ic { flex: none; width: 21px; height: 21px; border-radius: 7px; display: flex; align-items: center; justify-content: center; background: var(--mk-chip); color: var(--mk-ink-2); }
/* los dos primeros pasos leen de Stripe: llevan su color de marca */
.mk-astep-ic.is-brand { background: rgba(99, 91, 255, 0.12); color: #635bff; }
.theme--dark .mk-astep-ic.is-brand { background: rgba(140, 133, 255, 0.18); color: #8c85ff; }
.mk-astep-ic svg { width: 13px; height: 13px; fill: none; stroke: currentColor; stroke-width: 1.9; stroke-linecap: round; stroke-linejoin: round; }
.mk-astep-label { color: var(--mk-ink-2); min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
/* el dato devuelto es lo que demuestra que no se lo inventa: va destacado */
.mk-astep-val {
  margin-left: auto;
  flex: none;
  font-style: normal;
  font-weight: 600;
  font-size: 11px;
  color: var(--mk-ink);
  background: var(--mk-soft);
  border: 1px solid var(--mk-line-2);
  padding: 3px 8px;
  border-radius: 7px;
  white-space: nowrap;
}
.mk-astep-ok { flex: none; color: var(--mk-green); display: flex; }
.mk-astep-ok svg { width: 13px; height: 13px; fill: none; stroke: currentColor; stroke-width: 2.4; stroke-linecap: round; stroke-linejoin: round; }

.mk-adraft { padding: 13px 15px 14px; border-radius: 14px; background: var(--mk-accent-weak); }
.mk-adraft-h { display: flex; align-items: center; gap: 7px; font-size: 12px; font-weight: 700; color: var(--mk-accent); margin-bottom: 9px; }
.mk-adraft-time { margin-left: auto; font-style: normal; font-size: 11px; font-weight: 600; color: var(--mk-green-ink); background: var(--mk-green-weak); padding: 3px 9px; border-radius: 999px; }
.mk-adraft-meta { display: flex; flex-direction: column; gap: 2px; font-size: 11px; color: var(--mk-mut); padding-bottom: 9px; margin-bottom: 9px; border-bottom: 1px solid var(--mk-line-2); }
.mk-adraft p { margin: 0 0 8px; font-size: 12.5px; line-height: 1.55; color: var(--mk-ink); }
.mk-adraft .mk-prop-actions { margin-top: 12px; }

/* ---- follow-ups ---- */
.mk-follow { background: var(--mk-win); padding: 4px 0 0; }
.mk-follow-head { display: flex; align-items: center; gap: 12px; padding: 18px 26px; border-bottom: 1px solid var(--mk-line); }
.mk-follow-head b { font-size: 17px; }
.mk-tab--pill { padding: 6px 12px; border-radius: 9px; border-bottom: 0; }
.mk-tab--pill.is-active { background: var(--mk-accent-weak); }
.mk-follow-count { background: var(--mk-chip); padding: 2px 9px; border-radius: 8px; font-size: 12px; font-weight: 600; color: var(--mk-ink-2); }
.mk-follow-analyzing { color: var(--mk-mut); font-size: 12.5px; font-style: normal; }
.mk-follow-right { margin-left: auto; color: var(--mk-mut); font-size: 12.5px; }
.mk-follow-sort { color: var(--mk-ink-2); font-size: 12.5px; border: 1px solid var(--mk-line); padding: 6px 11px; border-radius: 9px; }
.mk-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.mk-table th { text-align: left; padding: 14px 18px; font-size: 10.5px; letter-spacing: 0.06em; color: var(--mk-mut); font-weight: 700; border-bottom: 1px solid var(--mk-line); }
.mk-table td { padding: 16px 18px; border-bottom: 1px solid var(--mk-line-2); vertical-align: top; }
.mk-check { display: block; width: 17px; height: 17px; border-radius: 5px; border: 1.5px solid var(--mk-faint); }
.mk-when { color: var(--mk-mut); white-space: nowrap; }
.mk-contact { font-weight: 600; white-space: nowrap; }
.mk-udot { display: inline-block; width: 9px; height: 9px; border-radius: 50%; margin-right: 9px; vertical-align: middle; }
.mk-udot.is-high { background: var(--mk-red); }
.mk-udot.is-med { background: var(--mk-amber); }
.mk-udot.is-low { background: var(--mk-faint); }
.mk-sum { color: var(--mk-ink-2); line-height: 1.5; max-width: 340px; }
.mk-rec { font-weight: 600; line-height: 1.5; max-width: 360px; }

/* =======================================================================
   Responsive: en lienzos estrechos se ocultan paneles y columnas en vez de
   encoger el escritorio completo, para que el texto siga siendo legible.
   ======================================================================= */

/* ---- tablet ---- */
.is-md .mk-inbox { grid-template-columns: 54px 300px 1fr; }
/* solo se ocultan los paneles DENTRO del buzón: las variantes de un solo panel
   (replies, chat) reutilizan .mk-read/.mk-chat y deben seguir visibles. */
.is-md .mk-inbox .mk-chat { display: none; }
.is-md .mk-follow-head { gap: 9px; padding: 15px 18px; }
.is-md .mk-follow-analyzing, .is-md .mk-follow-right { display: none; }
.is-md .mk-table th, .is-md .mk-table td { padding: 13px 14px; }
.is-md .mk-sum { max-width: 210px; }
.is-md .mk-rec { max-width: 240px; }
.is-md .mk-center { padding: 20px; }
.is-md .mk-collab { padding: 26px; }
.is-md .mk-collab-card { max-width: 470px; }
.is-md .mk-float .mk-av { width: 40px; height: 40px; font-size: 13px; }
.is-md .mk-dev--mac { width: 290px; height: 186px; top: 14%; }
.is-md .mk-dev--browser, .is-md .mk-dev--win { width: 170px; height: 116px; top: 29%; }
.is-md .mk-dev--linux { width: 130px; height: 86px; }
.is-md .mk-tools { padding: 24px; }
.is-md .mk-tools-card { max-width: 470px; }
.is-md .mk-agentp { max-width: 500px; }

/* ---- mobile ---- */
.is-sm .mk-inbox { grid-template-columns: 46px 1fr; }
.is-sm .mk-inbox .mk-read, .is-sm .mk-inbox .mk-chat { display: none; }
.is-sm .mk-rail { padding: 11px 0; gap: 11px; }
.is-sm .mk-logo { width: 26px; height: 26px; }
.is-sm .mk-rail-btn { width: 30px; height: 30px; }
.is-sm .mk-rail-btn svg { width: 17px; height: 17px; }
.is-sm .mk-list-head { padding: 12px 12px 8px; }
.is-sm .mk-list-head b { font-size: 15px; }
.is-sm .mk-tabs, .is-sm .mk-sort { padding-left: 12px; padding-right: 12px; }
.is-sm .mk-row { padding: 11px 12px; }
.is-sm .mk-row.is-active { margin: 4px 6px; }
.is-sm .mk-list-foot { padding: 10px 12px; }

.is-sm .mk-center { padding: 14px; align-items: center; }
.is-sm .mk-center .mk-panel { max-height: 100%; }
.is-sm .mk-read-head { padding: 14px 15px 12px; }
.is-sm .mk-read-body { padding: 6px 15px 12px; }
.is-sm .mk-suggest, .is-sm .mk-suggest--big { margin: 0; padding: 14px 15px 16px; }
.is-sm .mk-suggest--big .mk-suggest-h { font-size: 13.5px; }
.is-sm .mk-suggest--big .mk-opt { padding: 11px 13px; font-size: 12.5px; }
.is-sm .mk-edit-top { padding: 13px 15px; flex-wrap: wrap; gap: 8px; }
.is-sm .mk-edit-body { padding: 15px; font-size: 13px; }
.is-sm .mk-edit-actions { padding: 12px 15px; }

.is-sm .mk-follow-head { flex-wrap: wrap; gap: 8px; padding: 14px 15px; }
.is-sm .mk-follow-head b { font-size: 15px; }
.is-sm .mk-follow-analyzing, .is-sm .mk-follow-right, .is-sm .mk-follow-sort { display: none; }
.is-sm .mk-table th, .is-sm .mk-table td { padding: 12px 10px; }
.is-sm .mk-table th:nth-child(4), .is-sm .mk-table td:nth-child(4) { display: none; }
.is-sm .mk-table th:first-child, .is-sm .mk-table td:first-child { display: none; }
.is-sm .mk-rec { max-width: none; font-size: 12.5px; }
.is-sm .mk-when, .is-sm .mk-contact { font-size: 12px; }

/* el valor devuelto por cada paso no cabe en la misma línea que la etiqueta:
   baja a una segunda fila en vez de comer el texto del paso. */
.is-sm .mk-astep { flex-wrap: wrap; padding: 8px 10px; row-gap: 5px; }
.is-sm .mk-astep-label { flex: 1; white-space: normal; }
.is-sm .mk-astep-val { margin-left: 30px; order: 3; }
/* el check cierra la fila también aquí: sin orden explícito se colaba entre la
   etiqueta y el dato al reordenar este último. */
.is-sm .mk-astep-ok { order: 4; }
.is-sm .mk-agent-scroll { padding: 12px 12px 14px; gap: 9px; }
/* en móvil el lienzo es más bajo: sobra el mensaje intermedio de la IA */
.is-sm .mk-agent-answer { display: none; }

.is-sm .mk-collab { padding: 18px; }
.is-sm .mk-collab-card { max-width: none; padding: 16px 16px 18px; border-radius: 16px; }
.is-sm .mk-cc-subj { font-size: 13px; }
.is-sm .mk-cc-assign { display: none; }
/* en móvil la tarjeta ocupa casi todo el ancho: los avatares flotantes y el arco
   quedarían detrás, así que se ocultan en vez de asomar a medias. */
.is-sm .mk-float, .is-sm .mk-collab-arc { display: none; }

.is-sm .mk-plat-links { display: none; }
.is-sm .mk-dev--mac { width: 250px; height: 160px; top: 10%; }
.is-sm .mk-dev--browser { width: 126px; height: 90px; left: 4%; top: 44%; }
.is-sm .mk-dev--win { width: 126px; height: 90px; right: 4%; top: 44%; }
.is-sm .mk-dev--linux { width: 118px; height: 78px; top: 66%; }
.is-sm .mk-dev-tag { font-size: 9px; padding: 3px 8px; bottom: -11px; }
.is-sm .mk-os { width: 10px; height: 10px; }
.is-sm .mk-plat-caption { bottom: 16px; }
.is-sm .mk-plat-caption p { font-size: 11.5px; padding: 0 16px; }

.is-sm .mk-tools { padding: 16px; }
.is-sm .mk-tools-card { max-width: none; padding: 16px 16px 18px; border-radius: 16px; }
/* en el lienzo estrecho ocho servicios en cuatro columnas dejan chips ilegibles:
   dos columnas y solo los cuatro primeros (los que salen conectados). */
.is-sm .mk-tools-grid { grid-template-columns: repeat(2, 1fr); }
.is-sm .mk-svc:nth-child(n + 5) { display: none; }
</style>
